import { openMetadataApiClient } from '@/shared/api/core/axios-instance';
import {
  createQueryParams,
  createQuickFilterShouldClause,
  createShouldClause,
  createTermClause,
  type ElasticsearchQueryFilter,
  INDICES,
  QUERY_DEFAULTS,
  QUERY_FIELD_NAMES,
} from '@/shared/api/openmetadata';
import type { OpenMetadataResponse } from '@/shared/api/openmetadata';
import { escapeSearchQuery } from '@/shared/lib/sanitize';
import type { SortByOption } from '@/shared/types/options';

import {
  DISCOVERY_SEARCH_EXCLUDED_ASSET_TYPES,
  isExcludedFromDiscoveryAssetTypeFilter,
} from '@/entities/asset-search/domain/constants';
import type { FilterState } from '@/entities/discovery/domain/types';

/**
 * 필터 상태를 OpenMetadata 검색용 Elasticsearch 쿼리로 변환
 *
 * @description
 * 통합 검색에서 사용하는 필터를 Elasticsearch bool 쿼리로 변환합니다.
 * - DISCOVERY_SEARCH_EXCLUDED_ASSET_TYPES에 해당하는 entityType은 항상 결과에서 제외
 * - QuickFilter(트리 선택)와 일반 필터를 모두 지원
 * - Certification(성숙도)는 **`certification.tagLabel.tagFQN`** term 매칭
 *   (예: `certification.gold`, `certification.silver`) — 도메인/데이터자산 검색과 동일
 *
 * @param filters - 현재 필터 상태
 * @param availableServiceTypes - 사용 가능한 서비스 타입 목록 (dataSource 필터 처리용)
 * @returns Elasticsearch 쿼리 필터 또는 undefined
 */
const buildQueryFilter = (
  filters: FilterState,
  availableServiceTypes: string[] = []
): ElasticsearchQueryFilter | undefined => {
  const mustClauses: Array<Record<string, unknown>> = [];
  const mustNotClauses: Array<Record<string, unknown>> = [];

  DISCOVERY_SEARCH_EXCLUDED_ASSET_TYPES.forEach((excludedType) => {
    mustNotClauses.push(createTermClause(QUERY_FIELD_NAMES.ENTITY_TYPE, excludedType));
  });

  if (filters.quickFilter) {
    if (filters.quickFilter.serviceType) {
      mustClauses.push(createQuickFilterShouldClause('serviceType', [filters.quickFilter.serviceType]));
    }
    if (filters.quickFilter.serviceName) {
      mustClauses.push(createQuickFilterShouldClause('service.displayName.keyword', [filters.quickFilter.serviceName]));
    }
    if (filters.quickFilter.databaseName) {
      mustClauses.push(createQuickFilterShouldClause('database.displayName', [filters.quickFilter.databaseName]));
    }
    if (filters.quickFilter.schemaName) {
      mustClauses.push(createQuickFilterShouldClause('databaseSchema.displayName', [filters.quickFilter.schemaName]));
    }
    if (filters.quickFilter.entityType) {
      mustClauses.push(createQuickFilterShouldClause('entityType.keyword', [filters.quickFilter.entityType]));
    }
  }

  if (filters.assetType && filters.assetType.length > 0) {
    const filteredAssetTypes = filters.assetType.filter((type) => !isExcludedFromDiscoveryAssetTypeFilter(type));
    if (filteredAssetTypes.length > 0) {
      mustClauses.push(createShouldClause(QUERY_FIELD_NAMES.ENTITY_TYPE, filteredAssetTypes));
    }
  }

  if (filters.domain && filters.domain.length > 0) {
    mustClauses.push(createShouldClause('domains.name.keyword', filters.domain));
  }

  if (filters.owner && filters.owner.length > 0) {
    mustClauses.push(createShouldClause('owners.displayName.keyword', filters.owner));
  }

  if (filters.tags && filters.tags.length > 0) {
    const tagsOperator = filters.tagsOperator || 'OR';
    if (tagsOperator === 'AND') {
      filters.tags.forEach((tag) => {
        mustClauses.push(createTermClause('tags.tagFQN', tag));
      });
    } else {
      mustClauses.push(createShouldClause('tags.tagFQN', filters.tags));
    }
  }

  if (filters.tier && filters.tier.length > 0) {
    mustClauses.push(createShouldClause('tier.tagFQN', filters.tier));
  }

  if (filters.certification && filters.certification.length > 0) {
    mustClauses.push(createShouldClause(QUERY_FIELD_NAMES.CERTIFICATION_FQN, filters.certification));
  }

  if (!filters.quickFilter && filters.dataSource && filters.dataSource.length > 0) {
    const dataSourceShould: Array<{ term: Record<string, string> }> = [];
    const serviceTypeSet = new Set(availableServiceTypes.map((type) => type.toLowerCase()));

    filters.dataSource.forEach((source) => {
      const lowerSource = source.toLowerCase();
      if (serviceTypeSet.has(lowerSource)) {
        dataSourceShould.push({ term: { serviceType: lowerSource } });
      } else {
        dataSourceShould.push({ term: { 'service.displayName.keyword': source } });
      }
    });

    if (dataSourceShould.length > 0) {
      mustClauses.push({ bool: { should: dataSourceShould } });
    }
  }

  if (mustClauses.length === 0 && mustNotClauses.length === 0) {
    return undefined;
  }

  const boolClause: Record<string, unknown> = {};

  if (mustClauses.length > 0) {
    boolClause.must = mustClauses;
  }

  if (mustNotClauses.length > 0) {
    boolClause.must_not = mustNotClauses;
  }

  return {
    query: {
      bool: boolClause,
    },
  } as ElasticsearchQueryFilter;
};

const SORT_CONFIG: Record<SortByOption, { field: string; order: 'asc' | 'desc' }> = {
  lastUpdated: { field: 'updatedAt', order: 'desc' },
  lastUpdatedAsc: { field: 'updatedAt', order: 'asc' },
  name: { field: 'name.keyword', order: 'asc' },
};

export const searchOpenMetadata = async (
  query: string,
  filters: FilterState,
  from: number = 0,
  size: number = QUERY_DEFAULTS.SIZE.SEARCH_PAGE,
  sortBy: SortByOption = 'lastUpdated',
  availableServiceTypes: string[] = []
): Promise<OpenMetadataResponse> => {
  const sortConfig = SORT_CONFIG[sortBy];

  // OpenSearch 특수 문자 이스케이핑 (파싱 에러 방지)
  const escapedQuery = query ? escapeSearchQuery(query) : QUERY_DEFAULTS.Q;

  const baseParams = {
    q: escapedQuery,
    index: INDICES.DATA_ASSET,
    from,
    size,
    exclude_source_fields: 'columns',
  };

  const params = createQueryParams({
    ...baseParams,
    sort_field: sortConfig.field,
    sort_order: sortConfig.order,
  });

  const queryFilter = buildQueryFilter(filters, availableServiceTypes);
  if (queryFilter) {
    params.query_filter = JSON.stringify(queryFilter);
  }

  const response = await openMetadataApiClient.get<OpenMetadataResponse>(`/search/query`, {
    params,
    paramsSerializer: {
      encode: (value) => encodeURIComponent(value),
    },
  });

  return response.data;
};
